// useReports.js
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createAddonApi,
  createFeeApi,
  createPlanApi,
  deleteAddon,
  getAddons,
  getAccountSubscriptionByAccountId,
  deleteFee,
  getFee,
  getPaystackPlans,
  getPlanPrivileges,
  getSubscribtionFees,
  getAccountSubscriptions,
  getPlans,
  getSubscriptionTransactions,
  toggleAddonStatus,
  togglePlanStatus,
  updateAddonApi,
  updateFeeApi,
  updatePlanApi,
} from "../api/subscribtionDashboardApi";
import { useSnackbar } from "notistack";

export const usePlans = (params) => {
  return useQuery({
    queryKey: ["plans", params],
    queryFn: () => getPlans(params),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const usePlanPrivileges = () => {
  return useQuery({
    queryKey: ["planPrivileges"],
    queryFn: getPlanPrivileges,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const usePaystackPlans = () => {
  return useQuery({
    queryKey: ["paystackPlans"],
    queryFn: getPaystackPlans,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useAddons = (params) => {
  return useQuery({
    queryKey: ["addons", params],
    queryFn: () => getAddons(params),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useTogglePlanStatus = (params) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: togglePlanStatus,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["plans"] });
      console.log(`Plan ${variables.planId} status toggled successfully`);
      enqueueSnackbar("status toggled successfully", { variant: "success" });
    },
    onError: (error) => {
      console.error("Failed to toggle plan status:", error);
      enqueueSnackbar(error || "Failed to toggle plan status", {
        variant: "error",
      });
    },
  });
};

export const useCreatePlan = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (planBody) => {
      const res = await createPlanApi(planBody);
      if (!res || res.return !== 1) {
        throw new Error(res?.msg || "Failed to create plan");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plans"] });
      enqueueSnackbar("Plan created successfully!", { variant: "success" });
    },
    onError: (error) => {
      const message = error?.message || "Failed to create plan!";
      console.error("Create plan error:", message);
      enqueueSnackbar(message, { variant: "error" });
    },
  });
};

export const useCreateAddon = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (addonBody) => {
      const res = await createAddonApi(addonBody);
      if (!res || res.return !== 1) {
        throw new Error(res?.msg || "Failed to create addon");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addons"] });
      enqueueSnackbar("Addon created successfully!", { variant: "success" });
    },
    onError: (error) => {
      const message = error?.message || "Failed to create addon!";
      console.error("Create addon error:", message);
      enqueueSnackbar(message, { variant: "error" });
    },
  });
};

export const useUpdatePlan = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (planBody) => {
      const res = await updatePlanApi(planBody);
      if (!res || res.return !== 1) {
        throw new Error(res?.msg || "Failed to update plan");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plans"] });
      enqueueSnackbar("Plan updated successfully!", { variant: "success" });
    },
    onError: (error) => {
      const message = error?.message || "Failed to update plan!";
      console.error("Create plan error:", message);
      enqueueSnackbar(message, { variant: "error" });
    },
  });
};

export const useUpdateAddon = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (addonBody) => {
      const res = await updateAddonApi(addonBody);
      if (!res || res.return !== 1) {
        throw new Error(res?.msg || "Failed to update addon");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addons"] });
      enqueueSnackbar("Addon updated successfully!", { variant: "success" });
    },
    onError: (error) => {
      const message = error?.message || "Failed to update addon!";
      console.error("Update addon error:", message);
      enqueueSnackbar(message, { variant: "error" });
    },
  });
};

export const useCreateFee = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (planBody) => {
      const res = await createFeeApi(planBody);
      if (!res || res.return !== 1) {
        throw new Error(res?.msg || "Failed to create Fee");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fees"] });
      enqueueSnackbar("Fee created successfully!", { variant: "success" });
    },
    onError: (error) => {
      const message = error?.message || "Failed to create Feee!";
      console.error("Create Feee error:", message);
      enqueueSnackbar(message, { variant: "error" });
    },
  });
};

export const useFees = (params) => {
  return useQuery({
    queryKey: ["fees", params],
    queryFn: () => getSubscribtionFees(params),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useFee = (id) => {
  return useQuery({
    queryKey: ["fee", id],
    queryFn: () => getFee(id),
    refetchOnWindowFocus: true,
  });
};

export const useAccountSubscriptions = (params) => {
  return useQuery({
    queryKey: ["accountSubscriptions", params],
    queryFn: () => getAccountSubscriptions(params),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useAccountSubscriptionDetail = (accountId) => {
  return useQuery({
    queryKey: ["accountSubscriptionDetail", accountId],
    queryFn: () => getAccountSubscriptionByAccountId(accountId),
    enabled: Boolean(accountId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useSubscriptionTransactions = (params) => {
  return useQuery({
    queryKey: ["subscriptionTransactions", params],
    queryFn: () => getSubscriptionTransactions(params),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useToggleAddonStatus = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: toggleAddonStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addons"] });
      enqueueSnackbar("Addon status updated successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      const message = error?.message || "Failed to update addon status";
      console.error("Failed to toggle addon status:", error);
      enqueueSnackbar(message, { variant: "error" });
    },
  });
};

export const useDeleteAddon = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id) => deleteAddon(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addons"] });
      enqueueSnackbar("Addon deleted successfully!", { variant: "success" });
    },
    onError: (error) => {
      const message = error?.message || "Failed to delete addon!";
      console.error("Delete addon error:", message);
      enqueueSnackbar(message, { variant: "error" });
    },
  });
};

export const useDeleteFee = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id) => deleteFee(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fees"] });
      enqueueSnackbar("Fee  Deleted successfully!", { variant: "success" });
    },
    onError: (error) => {
      const message = error?.message || "Failed to create plan!";
      console.error("Create plan error:", message);
      enqueueSnackbar(message, { variant: "error" });
    },
  });
};

export const useUpdateFee = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (planBody) => {
      const res = await updateFeeApi(planBody);
      if (!res || res.return !== 1) {
        throw new Error(res?.msg || "Failed to update fee");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fees"] });
      enqueueSnackbar("Fee updated successfully!", { variant: "success" });
    },
    onError: (error) => {
      const message = error?.message || "Failed to update fee!";
      console.error("Create feee error:", message);
      enqueueSnackbar(message, { variant: "error" });
    },
  });
};
